import { getClerkInstance } from "@clerk/clerk-expo";
import { API_BASE_URL } from "@constants/urls";
import useClerkUser from "@hooks/useClerkUser";
import axios from "axios";
import { useState } from "react";
import { ScrollView, View } from "react-native";
import { Button, Divider, Text, TextInput, useTheme } from "react-native-paper";
import { SafeAreaView } from "react-native-safe-area-context";

type ModuleRow = {
	module: number;
	collectionId: string;
	status: "idle" | "loading" | "done" | "error";
	json?: string;
	error?: string;
};

const SESSIONS_ENDPOINT = `${API_BASE_URL}/assessments/v1/sessions`;

const KNOWN_COLLECTION_IDS: Record<number, number> = {
	1: 783,
	2: 784,
	3: 785,
	4: 786,
	5: 787,
	6: 788,
	7: 789,
	8: 790,
};

const MODULE_NUMBERS = [1, 2, 3, 4, 5, 6, 7, 8];

const DEFAULT_PARAMS = JSON.stringify(
	{
		projectId: 26,
		organizationId: 24,
		sessionType: "SUMMATIVE",
		sessionSubType: "PRACTICE_TEST",
	},
	null,
	2,
);

const getAuthToken = () =>
	getClerkInstance().session?.getToken({ template: "jwt-with-user" });

const copyToClipboard = async (text: string) => {
	console.info(text);
	if (typeof navigator !== "undefined" && navigator.clipboard) {
		await navigator.clipboard.writeText(text);
	}
};

const describeError = (error: unknown) => {
	const axiosError = error as {
		response?: { status?: number; data?: unknown };
		message?: string;
	};
	const status = axiosError.response?.status;
	const data = axiosError.response?.data;
	const body =
		typeof data === "object"
			? JSON.stringify(data)
			: (data ?? axiosError.message);
	return [status, body].filter(Boolean).join(" · ");
};

const USER_SESSIONS_PATH = (userId: number) =>
	`${API_BASE_URL}/assessments/v1/users/${userId}/sessions`;

// Exercises the exact call useProethicaSessionSync makes: GET the user's
// sessions per module collection (and a no-collectionId "all" variant) with the
// Clerk jwt-with-user token and no tenantid header. A session marked is_deleted
// on the backend should NOT come back here -> the module reverts to "Start".
const UserSessionsFetcher = () => {
	const theme = useTheme();
	const { userPublicMetadataId } = useClerkUser();

	const [rows, setRows] = useState<ModuleRow[]>(
		MODULE_NUMBERS.map((module) => ({
			module,
			collectionId: KNOWN_COLLECTION_IDS[module]
				? String(KNOWN_COLLECTION_IDS[module])
				: "",
			status: "idle",
		})),
	);
	const [allResult, setAllResult] = useState<{
		status: ModuleRow["status"];
		json?: string;
		error?: string;
	}>({ status: "idle" });

	const patchRow = (module: number, patch: Partial<ModuleRow>) =>
		setRows((prev) =>
			prev.map((row) => (row.module === module ? { ...row, ...patch } : row)),
		);

	const fetchSessions = async (collectionId?: number) => {
		const token = await getAuthToken();
		if (!token) {
			throw new Error("No Clerk jwt-with-user token (session not ready)");
		}
		const url = `${USER_SESSIONS_PATH(userPublicMetadataId)}${
			collectionId ? `?collectionId=${collectionId}` : ""
		}`;
		const response = await axios.get(url, {
			headers: { Authorization: token },
		});
		return response.data?.data ?? response.data;
	};

	const fetchModule = async (row: ModuleRow) => {
		if (!userPublicMetadataId) {
			patchRow(row.module, {
				status: "error",
				error: "No userId (publicMetadata.id) yet",
			});
			return;
		}
		const moduleCollectionId = Number(row.collectionId);
		if (!moduleCollectionId) {
			patchRow(row.module, {
				status: "error",
				error: "Set a collectionId first",
			});
			return;
		}
		patchRow(row.module, { status: "loading", error: undefined });
		try {
			const payload = await fetchSessions(moduleCollectionId);
			patchRow(row.module, {
				status: "done",
				json: JSON.stringify(payload, null, 2),
			});
		} catch (error) {
			patchRow(row.module, { status: "error", error: describeError(error) });
		}
	};

	const fetchAll = async () => {
		for (const row of rows) {
			if (row.collectionId) {
				await fetchModule(row);
			}
		}
	};

	const fetchCombined = async () => {
		if (!userPublicMetadataId) {
			setAllResult({
				status: "error",
				error: "No userId (publicMetadata.id) yet",
			});
			return;
		}
		setAllResult({ status: "loading" });
		try {
			const payload = await fetchSessions();
			setAllResult({ status: "done", json: JSON.stringify(payload, null, 2) });
		} catch (error) {
			setAllResult({ status: "error", error: describeError(error) });
		}
	};

	const statusColor = (status: ModuleRow["status"]) =>
		status === "done"
			? theme.colors.primary
			: status === "error"
				? theme.colors.error
				: theme.colors.onSurfaceVariant;

	return (
		<View style={{ gap: 16 }}>
			<Text variant="titleLarge">User sessions (GET)</Text>
			<Text
				variant="bodySmall"
				style={{ color: theme.colors.onSurfaceVariant }}
			>
				GET {USER_SESSIONS_PATH(userPublicMetadataId ?? 0)}?collectionId=...
			</Text>
			<Text
				variant="bodySmall"
				style={{ color: theme.colors.onSurfaceVariant }}
			>
				userId (publicMetadata.id): {userPublicMetadataId ?? "— not loaded —"}.
				Same call useProethicaSessionSync makes. "Fetch all" hits one collection
				per module; "Fetch combined" omits collectionId to see if the backend
				returns everything in one call. A deleted session should not appear.
			</Text>

			<View style={{ flexDirection: "row", gap: 12 }}>
				<Button mode="contained" onPress={fetchAll}>
					Fetch all (per collection)
				</Button>
				<Button
					mode="outlined"
					onPress={fetchCombined}
					loading={allResult.status === "loading"}
				>
					Fetch combined (no collectionId)
				</Button>
			</View>
			{allResult.status !== "idle" ? (
				<Text
					variant="bodySmall"
					style={{ color: statusColor(allResult.status) }}
				>
					{allResult.status === "error"
						? allResult.error
						: allResult.json
							? `combined: ${allResult.json.length} chars`
							: allResult.status}
				</Text>
			) : null}
			{allResult.json ? (
				<Button
					mode="text"
					onPress={() => copyToClipboard(allResult.json as string)}
				>
					Copy combined
				</Button>
			) : null}

			<Divider />

			{rows.map((row) => (
				<View key={row.module} style={{ gap: 8 }}>
					<View style={{ flexDirection: "row", alignItems: "center", gap: 12 }}>
						<Text variant="titleMedium" style={{ width: 96 }}>
							Module {row.module}
						</Text>
						<TextInput
							mode="outlined"
							label="collectionId"
							value={row.collectionId}
							onChangeText={(value) =>
								patchRow(row.module, { collectionId: value, status: "idle" })
							}
							keyboardType="number-pad"
							dense
							style={{ flex: 1 }}
						/>
						<Button
							mode="contained-tonal"
							loading={row.status === "loading"}
							onPress={() => fetchModule(row)}
						>
							Fetch
						</Button>
						{row.json ? (
							<Button
								mode="text"
								onPress={() => copyToClipboard(row.json as string)}
							>
								Copy
							</Button>
						) : null}
					</View>
					<Text variant="bodySmall" style={{ color: statusColor(row.status) }}>
						{row.status === "error"
							? row.error
							: row.json
								? `${row.json.length} chars fetched`
								: row.status}
					</Text>
					<Divider />
				</View>
			))}
		</View>
	);
};

const SessionFetcher = () => {
	const theme = useTheme();

	const [params, setParams] = useState(DEFAULT_PARAMS);
	const [rows, setRows] = useState<ModuleRow[]>(
		MODULE_NUMBERS.map((module) => ({
			module,
			collectionId: KNOWN_COLLECTION_IDS[module]
				? String(KNOWN_COLLECTION_IDS[module])
				: "",
			status: "idle",
		})),
	);

	const copyToken = async () => {
		const token = await getAuthToken();
		if (token) {
			await copyToClipboard(token);
		}
	};

	const patchRow = (module: number, patch: Partial<ModuleRow>) =>
		setRows((prev) =>
			prev.map((row) => (row.module === module ? { ...row, ...patch } : row)),
		);

	const fetchModule = async (row: ModuleRow) => {
		const moduleCollectionId = Number(row.collectionId);
		if (!moduleCollectionId) {
			patchRow(row.module, {
				status: "error",
				error: "Set a collectionId first",
			});
			return;
		}
		patchRow(row.module, { status: "loading", error: undefined });
		try {
			const body = {
				...JSON.parse(params),
				collectionId: moduleCollectionId,
			};
			const token = await getAuthToken();
			console.info("clerk token length", token?.length ?? 0);
			if (!token) {
				patchRow(row.module, {
					status: "error",
					error: "No Clerk jwt-with-user token (session not ready)",
				});
				return;
			}
			const response = await axios.post(SESSIONS_ENDPOINT, body, {
				headers: { Authorization: token },
			});
			const payload = response.data?.data ?? response.data;
			patchRow(row.module, {
				status: "done",
				json: JSON.stringify(payload, null, 2),
			});
		} catch (error) {
			patchRow(row.module, { status: "error", error: describeError(error) });
		}
	};

	const fetchAll = async () => {
		for (const row of rows) {
			if (row.collectionId) {
				await fetchModule(row);
			}
		}
	};

	const copyAll = () => {
		const combined = Object.fromEntries(
			rows
				.filter((row) => row.json)
				.map((row) => [`module-${row.module}`, JSON.parse(row.json as string)]),
		);
		copyToClipboard(JSON.stringify(combined, null, 2));
	};

	const statusColor = (status: ModuleRow["status"]) =>
		status === "done"
			? theme.colors.primary
			: status === "error"
				? theme.colors.error
				: theme.colors.onSurfaceVariant;

	return (
		<SafeAreaView style={{ flex: 1 }}>
			<ScrollView contentContainerStyle={{ padding: 16, gap: 16 }}>
				<UserSessionsFetcher />
				<Divider style={{ height: 2 }} />
				<Text variant="titleLarge">Session fetcher</Text>
				<Text
					variant="bodySmall"
					style={{ color: theme.colors.onSurfaceVariant }}
				>
					POST {SESSIONS_ENDPOINT}
				</Text>
				<Text
					variant="bodySmall"
					style={{ color: theme.colors.onSurfaceVariant }}
				>
					Clerk jwt-with-user token is sent as Authorization (no tenantid
					header, to satisfy CORS). Set the live collectionId per module, Fetch,
					then Copy. Responses are also logged to the console.
				</Text>

				<TextInput
					mode="outlined"
					label="Request params (merged into every fetch)"
					value={params}
					onChangeText={setParams}
					multiline
					numberOfLines={6}
					style={{ fontFamily: "monospace" }}
				/>

				<View style={{ flexDirection: "row", gap: 12 }}>
					<Button mode="contained" onPress={fetchAll}>
						Fetch all
					</Button>
					<Button mode="outlined" onPress={copyAll}>
						Copy all
					</Button>
					<Button mode="text" onPress={copyToken}>
						Copy token
					</Button>
				</View>

				<Divider />

				{rows.map((row) => (
					<View key={row.module} style={{ gap: 8 }}>
						<View
							style={{
								flexDirection: "row",
								alignItems: "center",
								gap: 12,
							}}
						>
							<Text variant="titleMedium" style={{ width: 96 }}>
								Module {row.module}
							</Text>
							<TextInput
								mode="outlined"
								label="collectionId"
								value={row.collectionId}
								onChangeText={(value) =>
									patchRow(row.module, { collectionId: value, status: "idle" })
								}
								keyboardType="number-pad"
								dense
								style={{ flex: 1 }}
							/>
							<Button
								mode="contained-tonal"
								loading={row.status === "loading"}
								onPress={() => fetchModule(row)}
							>
								Fetch
							</Button>
							{row.json ? (
								<Button
									mode="text"
									onPress={() => copyToClipboard(row.json as string)}
								>
									Copy
								</Button>
							) : null}
						</View>
						<Text
							variant="bodySmall"
							style={{ color: statusColor(row.status) }}
						>
							{row.status === "error"
								? row.error
								: row.json
									? `${row.json.length} chars fetched`
									: row.status}
						</Text>
						<Divider />
					</View>
				))}
			</ScrollView>
		</SafeAreaView>
	);
};

export default SessionFetcher;
